import { Button, Input } from "@legal-500/legal500-design-system";
import { getLanguageFlag } from "../Details/surveyDetails.utils";
import styles from "./QuestionLibrary.module.css";

// Per-locale label shown (by the renderer) on the "+ {label}" add button and the
// subject field caption of a `multi-testimonial` question. Mirrors the localized
// text editing pattern used for the question text / description in the modal,
// kept in its own file to avoid further inflating the already-oversized modal.
type SubjectLabelFieldProps = {
  defaultText: string;
  onChangeDefault: (value: string) => void;
  translatableLanguages: string[];
  translations: Record<string, string>;
  onChangeTranslation: (language: string, value: string) => void;
  /** Auto-translates the default text into a single language. */
  onAutoTranslateLanguage?: (language: string) => void;
  /** Language code currently being auto-translated (disables that button). */
  autoTranslatingLanguage?: string | null;
};

function SubjectLabelField({
  defaultText,
  onChangeDefault,
  translatableLanguages,
  translations,
  onChangeTranslation,
  onAutoTranslateLanguage,
  autoTranslatingLanguage,
}: SubjectLabelFieldProps) {
  const hasDefault = Boolean(defaultText.trim());

  return (
    <div className={styles.formFieldFull}>
      <label className={styles.fieldLabel}>Subject label</label>
      <span className={styles.rangeLabelsHint}>
        Shown on the respondent's "+ {defaultText.trim() || "Add"}" button and the subject field for
        each testimonial entry.
      </span>
      <Input
        label="Label"
        value={defaultText}
        onChange={event => onChangeDefault(event.target.value)}
        fullWidth
        placeholder="e.g. Add Lawyer | Barrister | Expert"
      />

      {translatableLanguages.length > 0 && (
        <div className={styles.translationList}>
          {translatableLanguages.map(language => (
            <div key={language} className={styles.translationField}>
              <div className={styles.translationFieldHeader}>
                <label
                  className={styles.translationLabel}
                  htmlFor={`subject-label-translation-${language}`}
                >
                  {language.toUpperCase()} {getLanguageFlag(language)}
                </label>
                {onAutoTranslateLanguage && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => onAutoTranslateLanguage(language)}
                    disabled={autoTranslatingLanguage === language || !hasDefault}
                  >
                    {autoTranslatingLanguage === language ? "Translating..." : "Auto-translate"}
                  </Button>
                )}
              </div>
              <Input
                id={`subject-label-translation-${language}`}
                value={translations[language] ?? ""}
                onChange={event => onChangeTranslation(language, event.target.value)}
                fullWidth
                placeholder={`Enter ${language.toUpperCase()} translation`}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export { SubjectLabelField };
